import { useOnboarding } from "./OnboardingContext";
import { Building2, Search, User, Mail, Phone, MapPin, Hash, ArrowLeft, ArrowRight, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import debounce from "lodash.debounce";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StepCoordonnees = () => {
  const { 
    billingInfo, 
    updateBillingInfo, 
    nextStep, 
    prevStep,
    getPackageDetails
  } = useOnboarding();
  
  const [searchMode, setSearchMode] = useState("name"); // "siret" or "name" - default to name
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [companyFound, setCompanyFound] = useState(false);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const pkg = getPackageDetails();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Debounced search for company name
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedNameSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await fetch(`${API_URL}/api/pappers/search/name?q=${encodeURIComponent(query)}&limit=8`);
        const data = await response.json();
        
        if (data.success && data.results.length > 0) {
          setSearchResults(data.results);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch (err) {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // Search by SIRET
  const searchBySiret = async (siret) => {
    const cleanSiret = siret.replace(/\s/g, "");
    if (cleanSiret.length !== 14) {
      setError("Le SIRET doit contenir 14 chiffres");
      return;
    }
    
    setIsSearching(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/pappers/search/siret/${cleanSiret}`);
      const data = await response.json();
      
      if (data.success && data.company) {
        fillCompanyData(data.company);
        setCompanyFound(true);
      } else {
        setError(data.error || "Entreprise non trouvée");
        setCompanyFound(false);
      }
    } catch (err) {
      setError("Erreur lors de la recherche");
    } finally {
      setIsSearching(false);
    }
  };

  // Select company from autocomplete
  const selectCompany = async (company) => {
    setShowDropdown(false);
    setSearchQuery(company.denomination);
    
    // Fetch full details using SIRET
    setIsSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/pappers/search/siret/${company.siret}`);
      const data = await response.json();
      
      if (data.success && data.company) {
        fillCompanyData(data.company);
        setCompanyFound(true);
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setIsSearching(false);
    }
  };

  // Fill form with company data
  const fillCompanyData = (company) => {
    const fullAddress = [
      company.adresse,
      company.code_postal,
      company.ville
    ].filter(Boolean).join(", ");
    
    // Get first director name if available
    let contactName = billingInfo.contactName;
    if (company.dirigeants && company.dirigeants.length > 0) {
      contactName = company.dirigeants[0].nom || contactName;
    }
    
    updateBillingInfo({
      companyName: company.denomination,
      siret: company.siret,
      address: fullAddress,
      vatNumber: company.numero_tva || "",
      legalForm: company.forme_juridique || "",
      contactName: contactName
    });
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCompanyFound(false);
    setError(null);
    
    if (searchMode === "name") {
      debouncedNameSearch(value);
    }
  };

  // Handle SIRET search submit
  const handleSiretSearch = (e) => {
    e.preventDefault();
    searchBySiret(searchQuery);
  };

  // Validate form
  const isFormValid = () => {
    return (
      billingInfo.companyName &&
      billingInfo.siret &&
      billingInfo.contactName &&
      billingInfo.email &&
      billingInfo.phone &&
      billingInfo.address
    );
  };

  if (!pkg) return null;

  return (
    <div data-testid="step-coordonnees" className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
          Étape 4
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] mb-4">
          Vos coordonnées
        </h1>
        <p className="font-body text-[#525252]">
          Recherchez votre entreprise pour pré-remplir automatiquement vos informations.
        </p>
      </div>

      {/* Company Search Section */}
      <div className="bg-[#F9F6F0] border border-[#E5E5E5] p-6 mb-8">
        <h3 className="font-heading text-lg text-[#0A0A0A] mb-4 flex items-center gap-2">
          <Building2 size={20} className="text-[#B89F7C]" />
          Recherche d'entreprise
        </h3>
        
        {/* Search Mode Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => { setSearchMode("siret"); setSearchQuery(""); setShowDropdown(false); }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              searchMode === "siret" 
                ? "bg-[#0A0A0A] text-white" 
                : "bg-white border border-[#E5E5E5] text-[#525252] hover:border-[#B89F7C]"
            }`}
          >
            Par SIRET
          </button>
          <button
            type="button"
            onClick={() => { setSearchMode("name"); setSearchQuery(""); setShowDropdown(false); }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              searchMode === "name" 
                ? "bg-[#0A0A0A] text-white" 
                : "bg-white border border-[#E5E5E5] text-[#525252] hover:border-[#B89F7C]"
            }`}
          >
            Par nom
          </button>
        </div>

        {/* Search Input */}
        <div className="relative">
          <form onSubmit={searchMode === "siret" ? handleSiretSearch : (e) => e.preventDefault()}>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={searchMode === "siret" ? "Entrez le numéro SIRET (14 chiffres)" : "Recherchez par nom d'entreprise..."}
                data-testid="input-company-search"
                className="w-full border border-[#E5E5E5] px-4 py-3 pr-12 font-body focus:outline-none focus:border-[#B89F7C] transition-colors"
              />
              {searchMode === "siret" ? (
                <button
                  type="submit"
                  disabled={isSearching}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#B89F7C] hover:text-[#0A0A0A] transition-colors"
                >
                  {isSearching ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
                </button>
              ) : (
                isSearching && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <Loader2 size={20} className="animate-spin text-[#B89F7C]" />
                  </div>
                )
              )}
            </div>
          </form>

          {/* Autocomplete Dropdown */}
          {showDropdown && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-[#E5E5E5] shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((company, index) => (
                <button
                  key={company.siret}
                  type="button"
                  onClick={() => selectCompany(company)}
                  className="w-full px-4 py-3 text-left hover:bg-[#F9F6F0] transition-colors border-b border-[#E5E5E5] last:border-b-0"
                >
                  <p className="font-body text-sm font-medium text-[#0A0A0A]">{company.denomination}</p>
                  <p className="font-body text-xs text-[#525252]">
                    SIRET: {company.siret} {company.ville && `• ${company.ville}`}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Success/Error Messages */}
        {companyFound && (
          <div className="mt-3 flex items-center gap-2 text-green-600">
            <CheckCircle size={16} />
            <span className="font-body text-sm">Entreprise trouvée et informations pré-remplies</span>
          </div>
        )}
        {error && (
          <div className="mt-3 flex items-center gap-2 text-red-600">
            <AlertCircle size={16} />
            <span className="font-body text-sm">{error}</span>
          </div>
        )}
      </div>

      {/* Company Information Form */}
      <div className="bg-white border border-[#E5E5E5] p-6 mb-8">
        <h3 className="font-heading text-lg text-[#0A0A0A] mb-6">
          Informations de l'entreprise
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="font-body text-sm text-[#525252] mb-1 block">
              Raison sociale *
            </label>
            <div className="relative">
              <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B89F7C]" />
              <input
                type="text"
                required
                value={billingInfo.companyName || ""}
                onChange={(e) => updateBillingInfo({ companyName: e.target.value })}
                data-testid="input-company-name"
                className="w-full border border-[#E5E5E5] pl-10 pr-4 py-3 font-body focus:outline-none focus:border-[#B89F7C] transition-colors"
                placeholder="Nom de l'entreprise"
              />
            </div>
          </div>

          <div>
            <label className="font-body text-sm text-[#525252] mb-1 block">
              SIRET *
            </label>
            <div className="relative">
              <Hash size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B89F7C]" />
              <input
                type="text"
                required
                value={billingInfo.siret || ""}
                onChange={(e) => updateBillingInfo({ siret: e.target.value })}
                data-testid="input-siret"
                className="w-full border border-[#E5E5E5] pl-10 pr-4 py-3 font-body focus:outline-none focus:border-[#B89F7C] transition-colors"
                placeholder="12345678901234"
              />
            </div>
          </div>

          <div>
            <label className="font-body text-sm text-[#525252] mb-1 block">
              N° TVA intracommunautaire
            </label>
            <input
              type="text"
              value={billingInfo.vatNumber || ""}
              onChange={(e) => updateBillingInfo({ vatNumber: e.target.value })}
              data-testid="input-vat"
              className="w-full border border-[#E5E5E5] px-4 py-3 font-body focus:outline-none focus:border-[#B89F7C] transition-colors"
              placeholder="FR12345678901"
            />
          </div>

          <div className="md:col-span-2">
            <label className="font-body text-sm text-[#525252] mb-1 block">
              Adresse *
            </label>
            <div className="relative">
              <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B89F7C]" />
              <input
                type="text"
                required
                value={billingInfo.address || ""}
                onChange={(e) => updateBillingInfo({ address: e.target.value })}
                data-testid="input-address"
                className="w-full border border-[#E5E5E5] pl-10 pr-4 py-3 font-body focus:outline-none focus:border-[#B89F7C] transition-colors"
                placeholder="Adresse complète"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information Form */}
      <div className="bg-white border border-[#E5E5E5] p-6 mb-8">
        <h3 className="font-heading text-lg text-[#0A0A0A] mb-6">
          Contact et signataire
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="font-body text-sm text-[#525252] mb-1 block">
              Nom du contact / signataire *
            </label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B89F7C]" />
              <input
                type="text"
                required
                value={billingInfo.contactName || ""}
                onChange={(e) => updateBillingInfo({ contactName: e.target.value })}
                data-testid="input-contact-name"
                className="w-full border border-[#E5E5E5] pl-10 pr-4 py-3 font-body focus:outline-none focus:border-[#B89F7C] transition-colors"
                placeholder="Prénom Nom"
              />
            </div>
          </div>

          <div>
            <label className="font-body text-sm text-[#525252] mb-1 block">
              Email *
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B89F7C]" />
              <input
                type="email"
                required
                value={billingInfo.email || ""}
                onChange={(e) => updateBillingInfo({ email: e.target.value })}
                data-testid="input-email"
                className="w-full border border-[#E5E5E5] pl-10 pr-4 py-3 font-body focus:outline-none focus:border-[#B89F7C] transition-colors"
                placeholder="contact@entreprise.com"
              />
            </div>
          </div>

          <div>
            <label className="font-body text-sm text-[#525252] mb-1 block">
              Téléphone *
            </label>
            <div className="relative">
              <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B89F7C]" />
              <input
                type="tel"
                required
                value={billingInfo.phone || ""}
                onChange={(e) => updateBillingInfo({ phone: e.target.value })}
                data-testid="input-phone"
                className="w-full border border-[#E5E5E5] pl-10 pr-4 py-3 font-body focus:outline-none focus:border-[#B89F7C] transition-colors"
                placeholder="+33 6 00 00 00 00"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-[#E5E5E5]">
        <button
          onClick={prevStep}
          data-testid="btn-prev-step"
          className="flex items-center gap-2 font-body text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
        >
          <ArrowLeft size={16} />
          Retour
        </button>
        <button
          onClick={nextStep}
          disabled={!isFormValid()}
          data-testid="btn-next-step"
          className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuer
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default StepCoordonnees;
